<template>
  <div v-if="aiEnabled" class="ai-chat-drawer-root">
    <aside class="ai-chat-drawer-panel" :class="{ 'ai-chat-drawer-open': open }">
      <header class="ai-chat-drawer-header">
        <h2>{{ $t('agent.assistant.title') }}</h2>
        <div class="ai-chat-header-controls">
          <div class="ai-chat-mode-selector">
            <button
              v-for="m in modes"
              :key="m.value"
              class="ai-chat-mode-btn"
              :class="{ active: executionMode === m.value }"
              :title="$t(m.titleKey)"
              @click="executionMode = m.value"
            >{{ $t(m.labelKey) }}</button>
          </div>
          <span
            class="ai-chat-ws-status"
            :class="{ connected: wsConnected }"
            :title="wsConnected ? $t('agent.status.connected') : $t('agent.status.disconnected')"
          ></span>
        </div>
        <button
          class="ai-chat-drawer-close"
          :aria-label="$t('agent.chat.close')"
          @click="open = false"
        >&times;</button>
      </header>

      <div class="ai-chat-drawer-body">
        <chat-window :messages="messages" :is-loading="isLoading" />
        <message-input
          :disabled="isLoading"
          :is-loading="isLoading"
          @send="onSend"
          @cancel="cancelRequest"
        />
      </div>
    </aside>

    <agent-confirm-modal />
  </div>
</template>

<script>
import { createChatClient } from './useChat'
import { getAgentSession } from './agentSession'
import { getSessionId, getSessionIdSync } from './useSessionId'
import { resolveFeatureFlag } from '@/shared/useFeatureFlag'
import Config from '@/shared/Config'
import ChatWindow from './ChatWindow.vue'
import MessageInput from './MessageInput.vue'
import AgentConfirmModal from './AgentConfirmModal.vue'

const MODE_KEYS = [
  { value: 'backend', labelKey: 'agent.mode.api.label', titleKey: 'agent.mode.api.title' },
  { value: 'frontend', labelKey: 'agent.mode.ui.label', titleKey: 'agent.mode.ui.title' },
  { value: 'hybrid', labelKey: 'agent.mode.auto.label', titleKey: 'agent.mode.auto.title' }
]

export default {
  components: { ChatWindow, MessageInput, AgentConfirmModal },
  data () {
    return {
      open: false,
      executionMode: 'hybrid',
      modes: MODE_KEYS,
      featureEnabled: false,
      isProjectContext: false,
      messages: [],
      isLoading: false,
      wsConnected: false
    }
  },
  watch: {
    open (val) {
      // Toggle a class on the root .fp-app so the main content shrinks
      const fpApp = document.querySelector('.fp-app')
      if (fpApp) {
        fpApp.classList.toggle('ai-panel-open', val)
      }
      // Notify agentSession so the header button reflects the state
      if (this._agentSession) {
        this._agentSession.setChatOpen(val)
      }
    }
  },
  computed: {
    aiEnabled () {
      return this.featureEnabled && this.isProjectContext
    }
  },
  created () {
    // Chat client uses the tab UUID (sync) — the composite session ID
    // is only needed for the gateway WS (handled by agentSession).
    const sessionId = getSessionIdSync()
    this._chatClient = createChatClient(sessionId)
    this._chatClient.onChange(({ messages, isLoading }) => {
      this.messages = messages
      this.isLoading = isLoading
    })

    // Use the shared agentSession singleton (manages WS lifecycle)
    this._agentSession = getAgentSession()
    this._agentSession.onChange(({ active, wsConnected }) => {
      this.wsConnected = wsConnected
      // WS deactivated → force close the ChatDrawer
      if (!active && this.open) {
        this.open = false
      }
    })

    // Listen for chat open requests from the header button
    this._agentSession.onOpenChat(() => {
      this.toggle()
    })
  },
  async mounted () {
    this.featureEnabled = await resolveFeatureFlag('ai_agent')
    const config = await Config()
    this.isProjectContext = !!config.PROJECT_ID

    if (!this.aiEnabled) return

    // agentSession.tryRecover is handled by AgentToggle.
    // If it recovered, the WS is already active and we can open.
    if (this._agentSession.isActive()) {
      this.wsConnected = true
    }
  },
  beforeUnmount () {
    // Don't deactivate the session here — AgentToggle owns the lifecycle.
    // The ChatDrawer is just a consumer.
  },
  methods: {
    async toggle () {
      if (!this.open) {
        // Opening the chat → activate agentSession if not active
        if (!this._agentSession.isActive()) {
          const sessionId = await getSessionId()
          await this._agentSession.activate(sessionId)
        }
        this.open = true
        this._chatClient.triggerGreeting()
      } else {
        this.open = false
      }
    },
    onSend (text) {
      this._chatClient.sendMessage(text, { executionMode: this.executionMode })
    },
    cancelRequest () {
      this._chatClient.cancelRequest()
    },
    async _waitForWsAndContinue () {
      // Build a context summary from the persisted messages so the agent
      // knows what it was doing before the cross-module navigation.
      // The backend session is rolled back on cancel, so the LLM has no
      // memory — we replay the key context in the continuation prompt.
      const userMessages = this.messages
        .filter(m => m.role === 'user')
        .map(m => m.content)
      const lastAssistant = [...this.messages]
        .reverse()
        .find(m => m.role === 'assistant' && m.content)

      // Capture current UI state so the agent knows where it landed
      const { executeDOMAction } = await import('./domExecutor')
      const uiState = await executeDOMAction('read_ui_state', {})

      const context = [
        'I navigated to a new page as you requested.',
        userMessages.length
          ? `Here is what the user originally asked: "${userMessages[userMessages.length - 1]}"`
          : '',
        lastAssistant
          ? `Your last response before navigation: "${lastAssistant.content.slice(0, 500)}"`
          : '',
        `Current UI state after navigation: ${JSON.stringify(uiState)}`,
        'Continue where you left off on this new page.'
      ].filter(Boolean).join('\n')

      // Wait for WS to be connected
      await new Promise(resolve => {
        const check = () => {
          if (this.wsConnected) resolve()
          else setTimeout(check, 300)
        }
        check()
      })

      // Cancel any in-flight agent loop via HTTP and wait for the backend
      // to fully stop before sending the continuation. This avoids two
      // concurrent requests on the same provider session.
      await this._chatClient.cancelRequest()

      // Small extra delay to let the provider session fully settle
      await new Promise(resolve => setTimeout(resolve, 300))

      this._chatClient.sendMessage(context, {
        executionMode: this.executionMode,
        hidden: true
      })
    }
  }
}
</script>

<style lang="less" scoped>
@panel-width: 520px;

// Panel — sits on the left, pushes the entire .fp-app to the right
.ai-chat-drawer-panel {
  position: fixed;
  top: 0;
  left: 0;
  width: @panel-width;
  height: 100vh;
  background: @white;
  border-right: 1px solid @grey_light;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  transform: translateX(-100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.ai-chat-drawer-open {
    transform: translateX(0);
  }
}

// Panel header
.ai-chat-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid @grey_light;
  background: @background-color_aside;
  flex-shrink: 0;

  h2 {
    font-size: 15px;
    font-weight: 600;
    color: @text-color;
    margin: 0;
  }
}

.ai-chat-header-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  margin-right: 16px;
}

.ai-chat-mode-selector {
  display: flex;
  border: 1px solid @grey_light;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
}

.ai-chat-mode-btn {
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  background: @white;
  color: @grey;
  cursor: pointer;
  transition: all 0.15s;
  line-height: 1.4;

  &:not(:last-child) {
    border-right: 1px solid @grey_light;
  }

  &:hover:not(.active) {
    background: @background-color_aside;
    color: @text-color;
  }

  &.active {
    background: @blue_800;
    color: @white;
  }
}

// WS connection status dot
.ai-chat-ws-status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: @grey_light;
  flex-shrink: 0;
  transition: background 0.3s;

  &.connected {
    background: @green;
  }
}

.ai-chat-drawer-close {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid @grey_light;
  border-radius: 6px;
  font-size: 18px;
  color: @grey;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: @background-color_aside;
    color: @text-color;
  }
}

// Panel body
.ai-chat-drawer-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// Responsive
@media (max-width: 768px) {
  .ai-chat-drawer-panel {
    width: 100vw;
  }
}
</style>

<!-- Global (unscoped) styles to shift the entire app when panel is open -->
<style lang="less">
@ai-panel-width: 520px;
@ai-transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);

// Make .fp-app a positioning context so all absolute children
// (view, fp-aside, sidebar) move with it. Then offset it to
// the right to make room for the AI panel on the left.
.fp-app {
  position: relative;
  left: 0;
  width: 100%;
  transition: left @ai-transition, width @ai-transition;

  // Sidebar transition must live outside ai-panel-open so it also
  // applies when the class is removed (panel closing).
  .sidebar.absolute {
    transition: left @ai-transition, margin-left 400ms cubic-bezier(0.5, 1, 0.5, 1) !important;
  }

  &.ai-panel-open {
    left: @ai-panel-width;
    width: calc(100% - @ai-panel-width);

    // The cookie button is position: fixed, so the offset above does not move it
    .fp-consent-button {
      left: @ai-panel-width + 20px;
    }

    // The sidebar overlay uses position: fixed; left: 0 — it escapes
    // the relative positioning of .fp-app. Offset it manually.
    .sidebar.absolute {
      left: @ai-panel-width !important;
    }

    // .projects-list is a sibling of .sidebar (not a child), positioned
    // absolute inside .fp-sidebar-content. JS sets its left via
    // getBoundingClientRect (viewport coords) but .fp-sidebar-content
    // is inside .fp-app which has left: 520px (relative), so the
    // absolute left is offset by that amount. Compensate.
    .fp-sidebar-content .projects-list {
      transform: translateX(-@ai-panel-width);
    }
  }
}
</style>
